import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import PrimaryButton from '../../../components/PrimaryButton';
import client from '../../../lib/feathers';
import NewVarietalForm from '../../../components/data-management/NewVarietalForm';
import DeleteConfirmationModal from '../../../components/data-management/DeleteConfirmationModal';

const wineColors = {
  /* Whites (light → dark) */
  'almost clear': '#D6D9AC',
  'pale straw': '#E0DDA0',
  'green yellow': '#C7CA83',
  'light green-gold': '#C9C67A',
  'platinum yellow': '#D7CD86',
  'pale yellow': '#E2D890',
  'pale gold': '#E6B14C',
  'golden': '#D4A017',
  'deep gold': '#E8A016',
  /* Rosé & Amber */
  'pale salmon': '#E9A372',
  'deep pink': '#CD2D50',
  'deep salmon': '#D3302B',
  'tawny': '#892507',
  /* Reds (light → dark) */
  'light ruby': '#B82040',
  'ruby': '#9B1B30',
  'garnet': '#7B1A2C',
  'pale ruby': '#8E0811',
  'deep ruby': '#6B0A1A',
  'deep violet': '#3C030A',
  'dark purple': '#2A0410',
  'deep purple': '#1C0508',
};

export default function Varietals() {
  const [varietals, setVarietals] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingVarietal, setEditingVarietal] = useState(null);
  const [deletingVarietal, setDeletingVarietal] = useState(null);
  const [loading, setLoading] = useState(true);

  const getVarietals = async () => {
    try {
      setLoading(true);
      const service = client.service('admin/varietal');
      const result = await service.find({
        query: {
          $limit: 1000,
          $sort: { name: 1 },
        },
      });
      setVarietals(result.data);
    } catch (e) {
      console.error('Error fetching varietals:', e);
      toast.error('Failed to load varietals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getVarietals();
  }, []);

  const onNewVarietal = (newVarietal) => {
    setVarietals([...varietals, newVarietal].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const onUpdateVarietal = (updatedVarietal) => {
    setVarietals(
      varietals
        .map((v) => (v.id === updatedVarietal.id ? updatedVarietal : v))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    setEditingVarietal(null);
  };

  const handleDelete = async () => {
    if (!deletingVarietal) return;
    try {
      const service = client.service('admin/varietal');
      await service.remove(deletingVarietal.id);
      setVarietals(varietals.filter((v) => v.id !== deletingVarietal.id));
      toast.success(`Deleted ${deletingVarietal.name}`);
      setDeletingVarietal(null);
    } catch (e) {
      console.error('Error deleting varietal:', e);
      toast.error('Failed to delete varietal');
    }
  };

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Varietals</h3>
        <PrimaryButton onClick={() => setShowCreateModal(true)}>
          New Varietal
        </PrimaryButton>
      </div>

      <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Name
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Color
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : varietals.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                  No varietals found
                </td>
              </tr>
            ) : (
              varietals.map((varietal) => (
                <tr key={varietal.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {varietal.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span
                        className="inline-block w-5 h-5 rounded-full border border-gray-300 mr-2"
                        style={{ backgroundColor: wineColors[varietal.color] || '#ccc' }}
                        title={varietal.color}
                      />
                      <span className="text-sm text-gray-700">
                        {varietal.color}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                    <button
                      onClick={() => setEditingVarietal(varietal)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeletingVarietal(varietal)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" />
            </div>
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
            >
              <NewVarietalForm
                onCancelClick={() => setShowCreateModal(false)}
                onNewVarietal={(newVarietal) => {
                  onNewVarietal(newVarietal);
                  setShowCreateModal(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingVarietal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" />
            </div>
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
            >
              <NewVarietalForm
                varietal={editingVarietal}
                onCancelClick={() => setEditingVarietal(null)}
                onUpdateVarietal={onUpdateVarietal}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingVarietal && (
        <DeleteConfirmationModal
          title="Delete Varietal"
          message={`Are you sure you want to delete "${deletingVarietal.name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeletingVarietal(null)}
        />
      )}
    </div>
  );
}
